<template>
  <div class="">
    <template v-if="refundDetails && !showReceiptDrawer">
      <DrawerHeader @close="emit('failed')">
        Refund
      </DrawerHeader>
      <div class="flex flex-1 flex-col space-y-2 px-6">
        <CustomAmountDisplay :value="amountToRefund" />

        <StatRow
          label="Maximum Refund"
          type="currency"
          :value="refundDetails.maximumRefund"
          large
        />
        <BaseNumpad v-model="amountToRefund" />
      </div>

      <div class="space-y-3 p-4">
        <ActionButtonPrimary
          v-if="
            refundDetails.transactionMethod === 'pos_cash'
          "
          :disabled="refundDisabled"
          class="w-full"
          @click="handleCashRefund"
          >Refund</ActionButtonPrimary
        >
        <ActionButtonPrimary
          v-else-if="
            refundDetails.transactionMethod ===
              'pos_card' && !cardholderNotPresentEnabled
          "
          :disabled="refundDisabled"
          class="w-full"
          @click="handleCardRefund(false)"
          >Refund</ActionButtonPrimary
        >
        <template v-else>
          <ActionButtonPrimary
            :disabled="refundDisabled"
            class="w-full"
            @click="handleCardRefund(false)"
            >Refund (Card)</ActionButtonPrimary
          >
          <ActionButtonSecondary
            :disabled="refundDisabled"
            class="w-full"
            @click="handleCardRefund(true)"
            >Refund (CNP)</ActionButtonSecondary
          >
        </template>
      </div>
    </template>
  </div>

  <TransactionDrawer
    v-if="receiptTransaction"
    :is-open="showReceiptDrawer"
    :transaction="receiptTransaction"
    @close="handleCloseReceiptDrawer"
  />
</template>

<script setup lang="ts">
import { prepareRefund } from "@/services/prepareRefund"
import type { PrepareRefundResponse } from "@/services/prepareRefund"
import {
  createRefund,
  type CreateRefundResponse,
} from "@/services/createRefund"
import { IPayment } from "@/androidPlugins/IPayment"

import { retrieveTransactionReceipt } from "@/services/transactions/retrieveTransactionReceipt"
import { IPrinter } from "@/androidPlugins/IPrinter"

const { startLoading, stopLoading } =
  usePageBlockingLoading()
const { cardholderNotPresentEnabled } =
  useCardholderNotPresent()
const { printingSupported } = useDevice()
const { tenant } = useTenant()
const props = defineProps<{ transactionId: string }>()
const emit = defineEmits(["success", "failed"])

const refundDetails = ref<PrepareRefundResponse>()
onMounted(async () => {
  startLoading()
  try {
    refundDetails.value = await prepareRefund(
      props.transactionId
    )

    if (refundDetails.value.isDccFieldsPopulated) {
      amountToRefund.value =
        refundDetails.value.maximumRefund
      handleCardRefund(false)
    }
  } catch {
    toast.error("Failed to prepare transaction for refund")
    emit("failed")
  } finally {
    stopLoading()
  }
})
const amountToRefund = ref(0)

const refundDisabled = computed(
  () => amountToRefund.value <= 0
)

const handleCashRefund = async () => {
  if (
    !refundDetails.value ||
    refundDetails.value.transactionMethod === "pos_card"
  ) {
    console.error(
      "Programming error: handleCashRefund should only be called on a prepared cash refund"
    )
    return
  }
  startLoading()

  if (
    amountToRefund.value > refundDetails.value.maximumRefund
  ) {
    toast.error("Refund amount exceeds maximum refund")
  } else {
    try {
      const refund = await createRefund(
        props.transactionId,
        {
          source: "pos_cash",
          data: {
            amount: amountToRefund.value,
          },
        }
      )
      if (refund.status === "successful") {
        await onSuccess(refund)
      } else {
        toast.error("Refund failed")
      }
    } catch {
      toast.error("Failed to create refund")
    }
  }

  stopLoading()
}

const handleCardRefund = async (
  cardholderNotPresent: boolean
) => {
  if (
    !refundDetails.value ||
    refundDetails.value.transactionMethod === "pos_cash"
  ) {
    console.error(
      "Programming error: handleCardRefund should only be called on a prepared card refund"
    )
    return
  }
  startLoading()

  if (
    amountToRefund.value > refundDetails.value.maximumRefund
  ) {
    toast.error("Refund amount exceeds maximum refund")
  } else {
    try {
      const {
        uti,
        maximumRefund,
        transactionMethod,
        ...refundParams
      } = refundDetails.value
      const deviceResponse =
        await IPayment.makeDeviceRefund({
          amountTrans: amountToRefund.value,
          disablePrinting:
            !tenant.isCardReceiptsEnabled ||
            !printingSupported.value,
          cardholderNotPresent,
          ...refundParams,
        })

      try {
        const refund = await createRefund(
          props.transactionId,
          {
            source: "pos_card",
            data: deviceResponse.data,
          }
        )

        switch (refund.status) {
          case "successful": {
            await onSuccess(refund)

            break
          }
          case "failed": {
            toast.error("Refund failed")
            await maybePrintMerchantReceipt(refund.id)

            break
          }
          case "cancelled": {
            toast.error("Refund cancelled")

            break
          }
          default: {
            toast.error("Refund failed")

            break
          }
          // No default
        }
      } catch {
        // Error in create_refund api call
        toast.info("Error logging refund result", 10000)
        emit("failed")
      }
    } catch {
      // Likely error handing over to payment application
      toast.error("Failed to refund")
    }
  }

  stopLoading()
}

const showReceiptDrawer = ref(false)
const receiptTransaction = ref<CreateRefundResponse>()
const onSuccess = async (refund: CreateRefundResponse) => {
  receiptTransaction.value = refund
  await maybePrintMerchantReceipt(refund.id)

  if (tenant.isTransactionReceiptsEnabled) {
    showReceiptDrawer.value = true
  } else {
    toast.success("Refund successful")
    emit("success")
  }
}

const handleCloseReceiptDrawer = () => {
  showReceiptDrawer.value = false
  emit("success")
}

const maybePrintMerchantReceipt = async (
  refundId: string
) => {
  if (
    tenant.isAutomaticMerchantReceiptEnabled &&
    printingSupported.value
  ) {
    startLoading()
    try {
      const { receipt } = await retrieveTransactionReceipt(
        refundId,
        {
          isMerchant: true,
        }
      )

      await IPrinter.printStr({ str: receipt })
    } catch {
      toast.error("Failed to generate receipt")
    } finally {
      stopLoading()
    }
  }
}
</script>
